import type { TicketEventMessage } from './events';

/** Field names that, on their own, aren't worth a notification. */
const NOISE_FIELDS = new Set([
  'area',
  'affectedAreaIds',
  'notifyListIds',
  'notifyEmails',
  'links',
  'resolvedAt',
]);

/**
 * Returns true when the event's only changes are bookkeeping and there's no
 * semantic action data to render. The SSE refresh still fires; we just don't
 * surface a toast/browser notification.
 */
export function shouldSuppressNotification(msg: TicketEventMessage): boolean {
  const p = msg.payload ?? {};
  if (
    p.toStatus ||
    p.toPriority ||
    p.toAssigneeName ||
    p.toAssigneeUserId ||
    p.commentPreview ||
    p.resolutionPreview ||
    p.blockedReasonPreview ||
    p.reopenReason ||
    p.newRank !== undefined ||
    p.deleted ||
    msg.type === 'ticket.created' ||
    msg.type === 'ticket.comment_added' ||
    msg.type === 'suggestion.created'
  ) {
    return false;
  }
  const fields = (p.changedFields as string[] | undefined) ?? [];
  if (fields.length === 0) return true;
  for (const f of fields) if (!NOISE_FIELDS.has(f)) return false;
  return true;
}

/**
 * Returns true if a notification for this event should be shown to the given
 * user (or if there is no signed-in user yet — fail open so we don't lose
 * notifications during the brief gap before the hello event lands).
 *   - Broadcast events (audience: 'all') always pass.
 *   - Otherwise the user must be in involvedUserIds or in mentionedUserIds.
 */
export function isInAudience(msg: TicketEventMessage, selfUserId: string | null): boolean {
  if (!selfUserId) return true;
  const p = msg.payload ?? {};
  if (p.audience === 'all') return true;
  const involved = Array.isArray(p.involvedUserIds) ? (p.involvedUserIds as string[]) : [];
  const mentioned = Array.isArray(p.mentionedUserIds) ? (p.mentionedUserIds as string[]) : [];
  return involved.includes(selfUserId) || mentioned.includes(selfUserId);
}

export interface RenderedNotification {
  title: string;
  body: string;
  /** Path the client should navigate to when the notification is activated. */
  path: string;
}

function truncate(s: string, max: number): string {
  return s.length > max ? s.slice(0, max - 1) + '…' : s;
}

function statusBody(actor: string, toStatus: string, p: Record<string, unknown>): string {
  switch (toStatus) {
    case 'IN_PROGRESS':
      return `${actor} started work on this`;
    case 'BLOCKED': {
      const r = p.blockedReasonPreview as string | undefined;
      return r ? `${actor} blocked — "${r}"` : `${actor} blocked this`;
    }
    case 'PENDING_CODE_REVIEW': {
      const r = p.resolutionPreview as string | undefined;
      return r ? `${actor} sent for code review — "${r}"` : `${actor} sent this for code review`;
    }
    case 'READY_TO_DEPLOY':
      return `${actor} marked Ready to Deploy`;
    case 'PENDING_CUSTOMER_REVIEW':
      return `${actor} deployed — pending customer review`;
    case 'PENDING_REPORTER_REVIEW':
      return `${actor} deployed — please verify`;
    case 'PENDING_VERIFICATION':
      return `${actor} flagged this for reminder / verification`;
    case 'FUTURE':
      return `${actor} moved this to Future Tickets`;
    case 'RESOLVED': {
      const r = p.resolutionPreview as string | undefined;
      return r ? `${actor} resolved — "${r}"` : `${actor} resolved this`;
    }
    case 'OPEN':
      return `${actor} re-opened this`;
    default:
      return `${actor} moved to ${toStatus}`;
  }
}

/**
 * Builds the user-facing title / body / navigation path for a single event.
 * Comment events deep-link to the Activity tab with a comment anchor so a
 * click lands the user on the actual reply rather than the overview.
 */
export function renderNotification(
  msg: TicketEventMessage,
  selfUserId: string | null,
): RenderedNotification {
  const p = msg.payload ?? {};
  const actor = (p.actorName as string | undefined) ?? null;

  if (msg.type === 'suggestion.created') {
    const sTitle = (p.title as string | undefined) ?? '';
    const by = actor ?? 'Someone';
    return {
      title: 'New suggestion',
      body: sTitle ? `${by}: ${truncate(sTitle, 80)}` : `${by} added a suggestion.`,
      path: '/suggestions',
    };
  }

  const ticketTitle = (p.title as string | undefined) ?? null;
  const heading = ticketTitle
    ? `#${msg.ticketId} ${truncate(ticketTitle, 50)}`
    : `Ticket #${msg.ticketId}`;
  const basePath = `/tickets/${msg.ticketId}`;

  if (p.deleted) {
    const who = actor ? `${actor} deleted this ticket.` : 'Ticket deleted.';
    return { title: `Deleted: #${msg.ticketId}`, body: who, path: '/' };
  }

  if (msg.type === 'ticket.created') {
    const prio = (p.priority as string | undefined) ?? '';
    const reporter = (p.reporterName as string | undefined) ?? '';
    const prefix = prio && prio !== 'NORMAL' ? `${prio} · ` : '';
    const who = reporter ? `${reporter} reported` : 'New ticket';
    const titleText = ticketTitle ? truncate(ticketTitle, 80) : '';
    return {
      title: `New: #${msg.ticketId}`,
      body: `${prefix}${who}: ${titleText}`.replace(/[: ]+$/, ''),
      path: basePath,
    };
  }

  if (msg.type === 'ticket.comment_added') {
    const preview = (p.commentPreview as string | undefined) ?? '';
    const mentions = Array.isArray(p.mentionedUserIds) ? (p.mentionedUserIds as string[]) : [];
    const mentioned = !!selfUserId && mentions.includes(selfUserId);
    const isPing = p.isPing === true;
    const by = actor ?? 'Someone';
    const verb = mentioned ? 'mentioned you' : isPing ? 'pinged for status' : 'commented';
    const body = preview ? `${by} ${verb}: ${preview}` : `${by} ${verb}.`;
    const commentId = (p.commentId as string | undefined) ?? null;
    const anchor = commentId ? `#comment-${commentId}` : '';
    return { title: heading, body, path: `${basePath}?tab=activity${anchor}` };
  }

  // ticket.updated — pick the most informative axis available.
  const actorWord = actor ?? 'Someone';

  const reopenReason = p.reopenReason as string | undefined;
  const toStatus = p.toStatus as string | undefined;
  if (toStatus === 'OPEN' && reopenReason) {
    return { title: heading, body: `${actorWord} re-opened — "${reopenReason}"`, path: basePath };
  }
  if (toStatus) {
    return { title: heading, body: statusBody(actorWord, toStatus, p), path: basePath };
  }

  const toPriority = p.toPriority as string | undefined;
  if (toPriority) {
    return { title: heading, body: `${actorWord} set priority to ${toPriority}`, path: basePath };
  }

  const toAssignee = p.toAssigneeName as string | undefined;
  const toAssigneeId = p.toAssigneeUserId as string | undefined;
  if (toAssignee != null || toAssigneeId != null) {
    if (!toAssignee) {
      return { title: heading, body: `${actorWord} unassigned this ticket`, path: basePath };
    }
    const youSuffix = selfUserId && toAssigneeId === selfUserId ? ' (you)' : '';
    return { title: heading, body: `${actorWord} assigned to ${toAssignee}${youSuffix}`, path: basePath };
  }

  const fields = (p.changedFields as string[] | undefined) ?? [];
  if (fields.length > 0) {
    return { title: heading, body: `${actorWord} updated ${humanizeFields(fields)}`, path: basePath };
  }
  return { title: heading, body: `${actorWord} made changes`, path: basePath };
}

function humanizeFields(fields: string[]): string {
  return fields
    .map((f) => {
      switch (f) {
        case 'attachments':
          return 'attachments';
        case 'stepsToReproduce':
          return 'steps to reproduce';
        case 'description':
          return 'description';
        case 'title':
          return 'title';
        case 'estimatedCompletion':
          return 'estimated completion';
        default:
          return f;
      }
    })
    .join(', ');
}

export function ordinal(n: number): string {
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`;
  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
}
